import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import { Badge, Box, Button, Popover, Tooltip } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TimestampSummaryIndicator from '../indicators/TimestampSummaryIndicator';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  const year = date.setLocale('da').toFormat('yyyy');
  return `${day} ${month} ${year}`;
};

function buildYearShortcuts(count = 4) {
  const now = DateTime.now(); // call DateTime.now() only once
  return Array.from({ length: count }, (_, i) => {
    const yearStart = now.minus({ years: i }).startOf('year');
    const yearEnd = yearStart.endOf('year');

    return {
      label: i === 0 ? 'Dette år' : String(yearStart.year),
      getValue: () => [yearStart, i === 0 ? now : yearEnd],
    };
  });
}

const esgShortcutItems = [...buildYearShortcuts(4)];

const shortcutsItems = [
  {
    label: '2025',
    getValue: () => [DateTime.now().startOf('year'), DateTime.now()],
  },
  {
    label: '2024',
    getValue: () => {
      const lastYearStart = DateTime.now().minus({ years: 1 }).startOf('year');
      const lastYearEnd = DateTime.now().minus({ years: 1 }).endOf('year');
      return [lastYearStart, lastYearEnd];
    },
  },
  // { label: 'Sidste uge', getValue: () => [DateTime.now().minus({ days: 7 }).startOf('week'), DateTime.now().minus({ days: 7 }).endOf('week')] },
  {
    label: 'Sidste 7 dage',
    getValue: () => [DateTime.now().minus({ days: 7 }), DateTime.now()],
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => [DateTime.now().minus({ weeks: 4 }), DateTime.now()],
  },
];

const DateRangeSelector = ({
  setTimeRange,
  timeRange,
  setDuration,
  timestampSummary,
  enableEsgShortcuts = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  const { t } = useTranslation('common');

  useEffect(() => {
    const updatedRange = [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ];
    setSelectedRange(updatedRange);
    setTempRange(updatedRange);
  }, [timeRange]);

  useEffect(() => {
    setDuration(selectedRange[1].diff(selectedRange[0], 'days').days);
  }, [selectedRange, setDuration]);

  const [tempRange, setTempRange] = useState(selectedRange);

  const handleRangeChange = (newRange) => {
    if (!newRange || !newRange[0] || !newRange[1]) return;
    setTempRange(newRange);
  };

  const handleConfirm = () => {
    if (!tempRange || !tempRange[0] || !tempRange[1]) return;

    const roundDownToNearestHour = (date) => date.startOf('day');
    let roundedStart = roundDownToNearestHour(tempRange[0]);
    let roundedEnd = tempRange[1].endOf('day');

    if (roundedEnd > DateTime.now()) {
      roundedEnd = DateTime.now();
    }
    if (
      roundedStart <= DateTime.now() &&
      roundedEnd <= DateTime.now() &&
      roundedEnd >= roundedStart
    ) {
      setSelectedRange([roundedStart, roundedEnd]);

      // Determine duration
      const durationDays = roundedEnd.diff(roundedStart, 'days').days;

      setTimeRange({
        start: roundedStart.toUTC().toMillis(),
        end: roundedEnd.toUTC().toMillis(),
      });

      setDuration(durationDays);
    }

    setAnchorEl(null);
  };

  const handleClick = (event) => {
    setTempRange(selectedRange); // Reset temp range on opening
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const validation = getTimestampSummaryValidationStatus(
    timeRange,
    timestampSummary,
  );

  // Check if current time range is outside bounds
  const isOutOfBounds = validation.isOutOfBounds;

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={t('locale')}
      localeText={
        t('locale') === 'da'
          ? daDK.components.MuiLocalizationProvider.defaultProps.localeText
          : null
      }
    >
      <Tooltip title="Vælg periode" enterDelay={500} placement="top">
        <Button
          startIcon={
            isOutOfBounds ? (
              <Badge
                badgeContent={
                  <WarningRoundedIcon fontSize="small" color="warning" />
                }
                sx={{ mr: 1 }}
              >
                <CalendarTodayRoundedIcon fontSize="small" />
              </Badge>
            ) : (
              <CalendarTodayRoundedIcon />
            )
          }
          variant="text"
          onClick={handleClick}
          disableRipple
          sx={{
            ':hover': {
              background: 'none',
            },
          }}
        >
          {selectedRange[0] && selectedRange[1]
            ? `${formatDate(selectedRange[0])} - ${formatDate(
                selectedRange[1],
              )}`
            : 'Vælg periode'}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
          <StaticDateRangePicker
            displayStaticWrapperAs="mobile"
            disableFuture
            value={tempRange}
            onChange={handleRangeChange}
            maxDateTime={DateTime.now()}
            views={['year', 'month', 'day', 'hours']}
            format="dd/MM/yyyy HH:mm"
            ampm={false}
            calendars={2}
            dayOfWeekFormatter={(day) =>
              day.toLocaleString({ weekday: 'short' })
            }
            slotProps={{
              shortcuts: {
                items: enableEsgShortcuts ? esgShortcutItems : shortcutsItems,
                style: { borderRadius: 2 },
              },
              actionBar: {
                actions: [], // Other options: 'cancel', 'today'
              },
            }}
          />
          <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
            <TimestampSummaryIndicator
              globalTimeRange={timeRange}
              timestampSummary={timestampSummary}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ p: 2, backgroundColor: 'background.paper' }}
          >
            {validation.isOutOfBounds && !validation.isInverted && (
              <Button
                startIcon={
                  <Badge
                    badgeContent={
                      <WarningRoundedIcon fontSize="small" color="warning" />
                    }
                    sx={{ mr: 1 }}
                  >
                    <CalendarTodayRoundedIcon fontSize="small" />
                  </Badge>
                }
                onClick={() => {
                  const [adjustedStart, adjustedEnd] = validation.adjustedRange;
                  setSelectedRange([adjustedStart, adjustedEnd]);
                  setTimeRange({
                    start: adjustedStart.toUTC().toMillis(),
                    end: adjustedEnd.toUTC().toMillis(),
                  });
                  setDuration(adjustedEnd.diff(adjustedStart, 'days').days);
                  setAnchorEl(null);
                }}
              >
                Tilpas periode
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{ ml: 1 }}
              disabled={!tempRange[0] || !tempRange[1]} // Prevent confirmation if range is incomplete
            >
              {t('buttonLabel.submit')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
